import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { scrollToId } from "@/lib/useLenis";
import { FOUNDED_YEAR } from "@/lib/site-data";

const EASE = [0.76, 0, 0.24, 1];
const HERO_IMG =
  "https://images.unsplash.com/photo-1729180253317-93ba9cf4ceb4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxiaHV0YW4lMjBtb25hc3RlcnklMjBtb3VudGFpbnxlbnwwfHx8fDE3ODQwNDE4MjB8MA&ixlib=rb-4.1.0&q=85";

const LineReveal = ({ children, delay = 0 }) => (
  <span className="mask-line">
    <motion.span
      initial={{ y: "105%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.05, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "-8%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#0A0B0A] text-[#F5F2EB]"
    >
      <HeroBackground yImg={yImg} scale={scale} />
      <HeroOverline />

      <motion.div
        style={{ y: yText }}
        className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-10 min-h-[100svh] flex flex-col justify-end pb-24 md:pb-32 will-change-transform"
      >
        <div className="mb-8 md:mb-10 max-w-3xl">
          <LineReveal delay={0.35}>
            <span className="inline-block text-[10px] md:text-[11px] uppercase tracking-[0.32em] text-[#D1492E] font-medium">
              A journey through the Last Shangri-La
            </span>
          </LineReveal>
        </div>

        <h1 className="font-serif font-light tracking-tighter text-[15vw] md:text-[10.5vw] leading-[0.86]">
          <LineReveal delay={0.55}>
            <span className="italic text-[#F5F2EB]/95">Where</span> the
          </LineReveal>{" "}
          <LineReveal delay={0.7}>mountains keep</LineReveal>{" "}
          <LineReveal delay={0.85}>
            <span className="text-[#D1492E]">their secrets.</span>
          </LineReveal>
        </h1>

        <HeroIntro />
      </motion.div>

      <HeroBottomAccents />
    </section>
  );
}

function HeroBackground({ yImg, scale }) {
  return (
    <motion.div style={{ y: yImg, scale }} className="absolute inset-0 will-change-transform">
      <img
        src={HERO_IMG}
        alt="Cliffside monastery in Bhutan"
        className="w-full h-full object-cover"
        draggable="false"
      />
      <div className="absolute inset-0 bg-[#0A0B0A]/55" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0B0A]/40 via-transparent to-[#0A0B0A]" />
    </motion.div>
  );
}

function HeroOverline() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.4, duration: 0.8 }}
      className="absolute top-24 md:top-28 inset-x-0 z-10"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-2 md:grid-cols-3 gap-6 items-start text-[10px] md:text-[11px] uppercase tracking-[0.28em] text-[#F5F2EB]/75">
        <div className="flex items-center gap-3">
          <span className="w-6 h-px bg-[#D1492E]" />
          <span>Est. Paro · Bhutan</span>
        </div>
        <div className="hidden md:block">27° 25′ 58″ N &nbsp; 89° 24′ 51″ E</div>
        <div className="text-right">Curated since {FOUNDED_YEAR}</div>
      </div>
    </motion.div>
  );
}

function HeroIntro() {
  return (
    <div className="mt-10 md:mt-14 grid md:grid-cols-[1fr_auto] gap-8 md:gap-16 items-end">
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.15, duration: 0.9, ease: EASE }}
        className="max-w-lg text-base md:text-lg leading-relaxed text-[#F5F2EB]/80 font-light"
      >
        Planet Air Adventure crafts private journeys through Bhutan — the
        Cultural Circuit, high-altitude treks, festival tsechus, and every
        flight into Paro. Slow, considered, unrepeatable.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.3, duration: 0.9, ease: EASE }}
        className="flex flex-col sm:flex-row gap-3"
      >
        <a
          href="#packages"
          onClick={(e) => { e.preventDefault(); scrollToId("packages"); }}
          data-testid="hero-cta-explore"
          className="btn-sharp inline-flex items-center gap-3 border border-[#F5F2EB] px-7 py-4 text-[11px] uppercase tracking-[0.22em] font-medium text-[#F5F2EB]"
        >
          Explore Journeys
          <span aria-hidden>↘</span>
        </a>
        <a
          href="#contact"
          onClick={(e) => { e.preventDefault(); scrollToId("contact"); }}
          data-testid="hero-cta-enquire"
          className="inline-flex items-center gap-3 px-2 py-4 text-[11px] uppercase tracking-[0.22em] font-medium text-[#F5F2EB]/85 hover:text-[#D1492E] transition-colors"
        >
          <span className="w-8 h-px bg-current" />
          Talk to a curator
        </a>
      </motion.div>
    </div>
  );
}

function HeroBottomAccents() {
  return (
    <>
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ delay: 1.6, duration: 1.2, ease: EASE }}
        style={{ transformOrigin: "left" }}
        className="absolute left-0 right-0 bottom-0 h-px bg-[#F5F2EB]/25 z-10"
      />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 right-6 md:right-10 z-10 text-[10px] uppercase tracking-[0.3em] text-[#F5F2EB]/60 flex items-center gap-3"
      >
        <span className="hidden md:inline">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          className="inline-block"
        >
          ↓
        </motion.span>
      </motion.div>
    </>
  );
}
